import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './preauths.css';
import type { PayerRuleSetting } from './types';
import {
  PREAUTH_TRAINING_TARGET,
  getSettingsSync,
  preauthsSentCountSync,
  restoreMany,
  togglePayer,
  toggleProcedure,
  usePreauthsVersion,
} from './store';
import { OWNER_NAME } from './utils';
import { SectionHeader } from '../settings-d/primitives';
import { SettingsChangeFooter, NumberSetting, recordSettingsChange, useCanEditSettings } from '../settings-d/settingsEdit';
import {
  DEFAULT_REMINDER_MONTHS,
  MAX_REMINDER_MONTHS,
  MIN_REMINDER_MONTHS,
  setReminderMonths,
  useReminderMonths,
} from './reminderSettings';

interface Toast {
  id: number;
  msg: string;
  undoLabel?: string;
  onUndo?: () => void;
}

/** Help line under every Prepare switch and procedure switch (Oct 22). */
const PREPARE_HELP = 'Turned off, ELVA stops preparing these. A visit that needs one still shows “Required pre-auth intentionally not obtained”.';

function Switch({ checked, label, disabled, onChange }: { checked: boolean; label: string; disabled?: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className="pa-switch"
      disabled={disabled}
      onClick={() => { onChange(!checked); }}
    />
  );
}

export function PreAuthsSettings() {
  usePreauthsVersion();
  const IS_OWNER = useCanEditSettings();
  const reminderMonths = useReminderMonths();
  const settings = getSettingsSync();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [wrongPayer, setWrongPayer] = useState<PayerRuleSetting | null>(null);
  const toastSeq = useRef(0);

  const addToast = (msg: string, undo?: { label: string; onUndo: () => void }) => {
    const id = ++toastSeq.current;
    setToasts((t) => [...t, { id, msg, ...(undo ? { undoLabel: undo.label, onUndo: undo.onUndo } : {}) }]);
    setTimeout(() => { setToasts((t) => t.filter((x) => x.id !== id)); }, 8000);
  };

  // Training line, computed from the pre-auths sent through ELVA (D300): the switch is in Settings › Autopilot.
  const sentThroughElva = preauthsSentCountSync();
  const autopilotLine = settings.autopilot
    ? `On since ${settings.autopilotSince ?? 'Oct 1'} · turned on by ${settings.autopilotBy ?? OWNER_NAME}`
    : sentThroughElva < PREAUTH_TRAINING_TARGET
      ? `${String(sentThroughElva)} of ${String(PREAUTH_TRAINING_TARGET)} pre-auths submitted. Your owner can turn autopilot on after the second.`
      : `${String(PREAUTH_TRAINING_TARGET)} pre-auths submitted. Your owner can turn this on.`;

  const payers = [...settings.payers].sort((a, b) => {
    if (a.nothingFound !== b.nothingFound) return a.nothingFound ? 1 : -1;
    return b.patients - a.patients;
  });

  const afterToggle = (label: string, removed: string[]) => {
    addToast(label);
    if (removed.length) {
      addToast(`Removed ${String(removed.length)} pre-auths that matched.`, {
        label: 'Undo',
        onUndo: () => { restoreMany(removed); },
      });
    }
  };

  return (
    <div className="pa" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {!IS_OWNER ? <p className="pa-meta" style={{ marginTop: 0 }}>Only owners and admins can change these.</p> : null}

      {/* Autopilot (Oct 22, D133, D300): NO switch here. The state, the training line and a link to
          Settings › Autopilot, where the switch, its confirm and its toast live. Only the owner turns it on. */}
      <section className="setd-card">
        <SectionHeader n="" title="Autopilot" />
        <div className="setd-row">
          <div style={{ minWidth: 0 }}>
            <div className="setd-row-help">
              ELVA sends each pre-auth as soon as it is ready. Anything missing a document, short on time or needing a decision
              still waits for you on the Pre-auths page.
            </div>
            <div className="setd-row-title" style={{ fontWeight: 500, marginTop: 8 }} data-testid="pa-autopilot-state">
              {autopilotLine}
            </div>
          </div>
          <div className="setd-row-right">
            <Link to="/settings/autopilot" className="setd-link">Go to Settings › Autopilot</Link>
          </div>
        </div>
      </section>

      {/* Reminder for approved pre-auths whose treatment isn't done (D228) */}
      <section className="setd-card">
        <SectionHeader n="" title="Approved pre-auths" />
        <div className="setd-row">
          <div style={{ minWidth: 0 }}>
            <div className="setd-row-title" style={{ fontWeight: 500, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 8px' }}>
              <span>Remind you when an approved pre-auth’s treatment isn’t done after</span>
              <NumberSetting
                label="Months before ELVA reminds you that an approved pre-auth’s treatment isn’t done"
                value={reminderMonths}
                min={MIN_REMINDER_MONTHS}
                max={MAX_REMINDER_MONTHS}
                unit="months"
                suffix=" months"
                readOnly={!IS_OWNER}
                onCommit={(n) => {
                  setReminderMonths(n);
                  recordSettingsChange('preauth-reminder', 'Pre-auth treatment reminder');
                }}
              />
              {IS_OWNER ? null : <span style={{ marginLeft: -6 }}>.</span>}
            </div>
            <div className="setd-row-help">ELVA’s default is {DEFAULT_REMINDER_MONTHS} months. It applies from today on.</div>
          </div>
        </div>
        <SettingsChangeFooter id="preauth-reminder" initial="Oct 1" />
      </section>

      {/* Payers */}
      <section className="setd-card">
        <SectionHeader n="" title={`Payers you work with · ${String(payers.length)}`} />
        <p className="setd-desc">
          ELVA researched what each payer requires when you joined on {settings.onboardingDate}, and set these to match.
          Turn off anything you don’t want pre-auths for, even when the payer requires one.
        </p>

        <div style={{ marginTop: 10 }}>
          {payers.map((p) => {
            const isOpen = expanded[p.id] ?? false;
            return (
              <div key={p.id} className="pa-set-row">
                <div>
                  <div style={{ fontWeight: 600 }}>{p.name}</div>
                  {p.adminThrough ? <div className="pa-meta">{p.adminThrough}</div> : null}
                </div>
                <div>
                  {p.nothingFound ? (
                    <>
                      <div style={{ fontWeight: 500 }}>Nothing found</div>
                      <div className="pa-meta">ELVA found no procedure {p.short} requires a pre-auth for</div>
                    </>
                  ) : p.prepareChangedBy ? (
                    <>
                      <div>{p.requiresWords}</div>
                      <div className="pa-meta">{p.prepareChangedBy}</div>
                    </>
                  ) : (
                    <>
                      <div>{p.requiresWords}</div>
                      <div className="pa-meta" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span>{p.source}</span>
                        {p.notInManual ? <span className="pa-badge b-warn">Not in the manual</span> : null}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        <button type="button" className="pa-src" onClick={() => { setWrongPayer(p); }}>· This isn’t right</button>
                      </div>
                    </>
                  )}
                </div>
                <div style={{ justifySelf: 'end', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  {p.nothingFound ? (
                    <span className="pa-badge b-mute">Nothing to prepare</span>
                  ) : (
                    <>
                      <Switch
                        checked={p.prepare}
                        label={`Prepare pre-auths for ${p.short}`}
                        disabled={!IS_OWNER}
                        onChange={(v) => {
                          const removed = togglePayer(p.id, v);
                          afterToggle(`${p.short} pre-auths: ${v ? 'on' : 'off'}.`, removed);
                        }}
                      />
                      <div className="pa-meta" style={{ maxWidth: 260, textAlign: 'right' }}>{PREPARE_HELP}</div>
                    </>
                  )}
                </div>
                <div style={{ justifySelf: 'end' }}>
                  {p.nothingFound ? null : (
                    <button
                      type="button"
                      className="pa-chev"
                      aria-expanded={isOpen}
                      aria-controls={`pa-set-inner-${p.id}`}
                      aria-label={isOpen ? `Hide ${p.short} procedures` : `Show ${p.short} procedures`}
                      onClick={() => { setExpanded((e) => ({ ...e, [p.id]: !isOpen })); }}
                    >
                      {isOpen ? '▾' : '▸'}
                    </button>
                  )}
                </div>

                {isOpen && !p.nothingFound ? (
                  <div className="pa-set-inner" id={`pa-set-inner-${p.id}`}>
                    {p.procedures.map((proc) => (
                      <div key={proc.id} className={`pa-set-proc${!p.prepare ? ' greyed' : ''}`}>
                        <div style={{ flex: '1 1 auto' }}>
                          <div style={{ fontWeight: 600 }}>{proc.name}</div>
                          <div className="pa-meta">{proc.codes} · {proc.setBy}</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                          <Switch
                            checked={proc.on}
                            label={`${proc.name} for ${p.short}`}
                            disabled={!IS_OWNER || !p.prepare}
                            onChange={(v) => {
                              const removed = toggleProcedure(p.id, proc.id, v);
                              afterToggle(`${p.short} ${proc.name.toLowerCase()}: ${v ? 'on' : 'off'}.`, removed);
                            }}
                          />
                          <div className="pa-meta" style={{ maxWidth: 260, textAlign: 'right' }}>{PREPARE_HELP}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <p className="pa-meta" style={{ marginTop: 16 }}>
          A payer appears here the first time one of your patients has it. “This isn’t right” sends a note to the ELVA team;
          we check it and correct the payer’s rules for you.
        </p>
      </section>

      <p className="pa-meta" style={{ marginTop: 14 }}>
        Changes save as you make them, and apply to appointments booked from now on.{' '}
        <Link to="/settings/payers" className="pa-src" style={{ textDecoration: 'underline' }}>See every rule ELVA knows in Payer Rules</Link>
      </p>

      {/* this isn't right */}
      {wrongPayer ? <ThisIsntRight payer={wrongPayer} onClose={() => { setWrongPayer(null); }} onSend={(name) => { setWrongPayer(null); addToast(`Thanks — the ELVA team will check this and update ${name}’s rules.`); }} /> : null}

      {toasts.length > 0 ? (
        <div className="pa-toasts">
          {toasts.map((t) => (
            <div key={t.id} className="pa-toast" role="status">
              <span>{t.msg}</span>
              {t.undoLabel ? (
                <button type="button" className="pa-toast-undo" onClick={() => { t.onUndo?.(); setToasts((list) => list.filter((x) => x.id !== t.id)); }}>{t.undoLabel}</button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ThisIsntRight({ payer, onClose, onSend }: { payer: PayerRuleSetting; onClose: () => void; onSend: (name: string) => void }) {
  const [text, setText] = useState('');
  return (
    <div className="pa-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pa-dialog" role="dialog" aria-modal="true" aria-label={`What’s wrong with ${payer.name}’s rules?`}>
        <h2 className="pa-dialog-title">What’s wrong with {payer.name}’s rules?</h2>
        <div>
          <span className="pa-label">Tell us what {payer.name} requires, and how you know.</span>
          <textarea className="pa-field" style={{ minHeight: 100 }} value={text} onChange={(e) => { setText(e.target.value); }} />
        </div>
        <button type="button" className="pa-btn pa-btn-sm" style={{ alignSelf: 'flex-start' }}>Attach a file (optional)</button>
        <div className="pa-dialog-actions">
          <button type="button" className="pa-btn pa-btn-primary" onClick={() => { onSend(payer.name); }}>Send to ELVA</button>
          <button type="button" className="pa-btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
